import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Plus, X, Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const toInputDate = (d) => new Date(d).toISOString().split('T')[0];

export default function JournalEntryForm() {
  const navigate = useNavigate();
  const { success, error: showError, ToastContainer } = useToast();
  
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    journalDate: toInputDate(new Date()),
    journalNumber: '',
    reference: '',
    description: '',
  });
  const [entries, setEntries] = useState([
    { id: 1, nominalAccountId: '', accountNumber: '', amount: '', description: '' },
    { id: 2, nominalAccountId: '', accountNumber: '', amount: '', description: '' },
  ]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await accountingAPI.getAccounts({ active_only: 'true' });
      setAccounts(data || []);
    } catch (err) {
      showError('Error', err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = () => {
    setEntries([...entries, { id: Date.now(), nominalAccountId: '', accountNumber: '', amount: '', description: '' }]);
  };

  const handleRemoveEntry = (id) => {
    if (entries.length <= 2) {
      showError('Error', 'At least 2 entries are required (debits must equal credits)');
      return;
    }
    setEntries(entries.filter(e => e.id !== id));
  };

  const handleEntryChange = (id, field, value) => {
    setEntries(entries.map(entry => {
      if (entry.id === id) {
        const updated = { ...entry, [field]: value };
        // Update account number when account is selected
        if (field === 'nominalAccountId') {
          const account = accounts.find(a => a.id === value);
          updated.accountNumber = account?.accountNumber || '';
        }
        return updated;
      }
      return entry;
    }));
  };

  const calculateTotal = () => {
    const amounts = entries
      .map(e => parseFloat(e.amount) || 0)
      .filter(a => !isNaN(a));
    return amounts.reduce((sum, amt) => sum + amt, 0);
  };

  const total = calculateTotal();
  const isBalanced = Math.abs(total) < 0.01; // Allow for small rounding differences

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.journalNumber) {
      showError('Error', 'Journal number is required');
      return;
    }

    if (!formData.journalDate) {
      showError('Error', 'Journal date is required');
      return;
    }

    if (!isBalanced) {
      showError('Error', `Journal entries must balance. Current total: ${currency(total)}. Debits must equal credits.`);
      return;
    }

    const validEntries = entries.filter(e => e.nominalAccountId && e.amount);
    if (validEntries.length < 2) {
      showError('Error', 'At least 2 valid entries are required');
      return;
    }

    // Validate all entries have accounts and amounts
    for (const entry of entries) {
      if (entry.nominalAccountId && (!entry.amount || parseFloat(entry.amount) === 0)) {
        showError('Error', 'All entries must have a valid amount');
        return;
      }
      if (entry.amount && !entry.nominalAccountId) {
        showError('Error', 'All entries with amounts must have an account selected');
        return;
      }
    }

    try {
      setSaving(true);
      const journalData = {
        journalDate: formData.journalDate,
        journalNumber: formData.journalNumber,
        reference: formData.reference || undefined,
        description: formData.description || undefined,
        entries: validEntries.map(entry => ({
          nominalAccountId: entry.nominalAccountId,
          amount: parseFloat(entry.amount),
          description: entry.description || undefined,
        })),
      };

      await accountingAPI.createJournal(journalData);
      success('Success', 'Journal entry created successfully');
      navigate('/tenant/reports/journals');
    } catch (err) {
      showError('Error', err.message || 'Failed to create journal entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <Layout>
        {/* Header */}
        <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                  New Journal Entry
                </h1>
                <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                  Create a manual journal entry (debits must equal credits)
                </p>
              </div>
              <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/reports/journals')}
                >
                  ← Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Journal Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Journal Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Journal Date <span className="text-[var(--ds-status-danger)]">*</span>
                      </label>
                      <Input
                        type="date"
                        value={formData.journalDate}
                        onChange={(e) => setFormData({ ...formData, journalDate: e.target.value })}
                        required
                        className="h-8"
                      />
                    </div>
                    <div>
                      <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Journal Number <span className="text-[var(--ds-status-danger)]">*</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.journalNumber}
                        onChange={(e) => setFormData({ ...formData, journalNumber: e.target.value })}
                        placeholder="JRN-001"
                        required
                        className="h-8"
                      />
                    </div>
                    <div>
                      <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reference
                      </label>
                      <Input
                        type="text"
                        value={formData.reference}
                        onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                        className="h-8"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="text-xs"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Journal Entries */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Journal Entries</CardTitle>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddEntry}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Entry
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-[var(--ds-accent-primary)]" />
                      <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading accounts...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Table>
                        <TableHeader>
                          <TableRow isHeader>
                            <TableHead>Account</TableHead>
                            <TableHead align="right">Amount</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead align="center" className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entries.map((entry, index) => (
                            <TableRow key={entry.id}>
                              <TableCell>
                                <Select
                                  value={entry.nominalAccountId}
                                  onChange={(e) => handleEntryChange(entry.id, 'nominalAccountId', e.target.value)}
                                  required={!!entry.amount}
                                  className="h-8"
                                >
                                  <option value="">Select account</option>
                                  {accounts.map(account => (
                                    <option key={account.id} value={account.id}>
                                      {account.accountNumber} - {account.accountName}
                                    </option>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={entry.amount}
                                  onChange={(e) => handleEntryChange(entry.id, 'amount', e.target.value)}
                                  placeholder="0.00"
                                  className="h-8 text-right"
                                  required={!!entry.nominalAccountId}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="text"
                                  value={entry.description}
                                  onChange={(e) => handleEntryChange(entry.id, 'description', e.target.value)}
                                  placeholder="Optional description"
                                  className="h-8"
                                />
                              </TableCell>
                              <TableCell align="center">
                                {entries.length > 2 && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon-sm"
                                    onClick={() => handleRemoveEntry(entry.id)}
                                    title="Remove entry"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {/* Balance Indicator */}
                      <div className={`mt-4 p-4 rounded-[var(--ds-radius-md)] border ${
                        isBalanced 
                          ? 'bg-[var(--ds-status-success-bg)] border-[var(--ds-status-success)]/30' 
                          : 'bg-[var(--ds-status-danger-bg)] border-[var(--ds-status-danger)]/30'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isBalanced ? (
                              <>
                                <svg className="h-5 w-5 text-[var(--ds-status-success)]" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs font-medium text-[var(--ds-status-success)]">Entries are balanced</span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="h-5 w-5 text-[var(--ds-status-danger)]" />
                                <span className="text-xs font-medium text-[var(--ds-status-danger)]">Entries are not balanced</span>
                              </>
                            )}
                          </div>
                          <span className={`text-xs font-semibold ${
                            isBalanced 
                              ? 'text-[var(--ds-status-success)]' 
                              : 'text-[var(--ds-status-danger)]'
                          }`}>
                            Total: {currency(total)}
                          </span>
                        </div>
                        {!isBalanced && (
                          <p className="mt-2 text-xs text-[var(--ds-status-danger)]">
                            Debits must equal credits. Adjust entry amounts to balance.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/reports/journals')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={saving || !isBalanced}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Journal Entry'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Layout>
    </>
  );
}
